import React from 'react';
import i18next from 'i18next';
import jmespath from "jmespath";

const caps = (str) => {
    let [l, ...rest] = str.split('');
    return [l.toUpperCase(), ...rest].join('');
};

const error_required = (k) => i18next.t('widget.input.value.required', { value: caps(k) });

export function conditionnalConfig(condition, form) {
    return jmespath.search({...form}, condition)
}


const check_form_validity = (form, keys) => {
    const errors = {};
    for (let k in keys) { // eslint-disable-line no-unused-vars
        // validator is deprecated
        const { validators = [], validator = null, required = false, allow_blank = false, conditional_display = {} } = keys[k];

        if (required && !form[k]) {
            if(allow_blank && form[k] !== '') {
                errors[k] = (error_required(k));
            }
        }

        if (k in form) {
            if (Array.isArray(validators) && validators.length) {
                validators.forEach((v) => {
                    const {result = true, message} = v(form[k], form, {conditional_display, required});
                    if (!result) {
                        errors[k] = message;
                    }
                })
            } else {
                if (validator) {
                    const {result = true, message} = validator(form[k]);
                    if (!result) {
                        errors[k] = message;
                    }
                }
            }
        }
    }

    return {
        valid: !Object.keys(errors).length,
        errors
    };
};

export default function useForm(keys, { allowCustom = false, displayInitialErrors = false, validateOnChange = true, clearOnChange = true, onChange} = {}) {
    const [form, setForm] = React.useState({});
    const [valid, setValid] = React.useState(true);
    const [errors, setErrors] = React.useState({});

    const validate = React.useCallback((formToValidate) => {
        const { valid, errors } = check_form_validity(formToValidate, keys);
        setValid(valid);
        setErrors(errors);
        return { valid, errors };
    }, [keys]);

    const public_validate = React.useCallback(() => {
        return validate(form);
    }, [form, validate]);

    React.useEffect(() => {
        const initialForm = Object.fromEntries(Object.entries(keys).map(([key, { value = null }]) => {
            return [key, value];
        }));

        const { valid: initialValid, errors: initialError } = check_form_validity(initialForm, keys);

        setForm(initialForm);
        setValid(initialValid);
        setErrors(displayInitialErrors ? initialError : {});
    }, [keys, displayInitialErrors]);

    const handleClearAndErrors = React.useCallback((newForm, name) => {
        // Clear on change and validate on change cannot be set to true together
        if (validateOnChange && !clearOnChange) {
            validate(newForm);
        }

        if (clearOnChange && name && errors[name] && !validateOnChange) {
            let newErrors = { ...errors };
            delete newErrors[name];
            setErrors(newErrors);
        }
    }, [validateOnChange, clearOnChange, errors, validate])

    const setFormValue = React.useCallback((name, value) => {
        const keys_list = Object.keys(keys);
        if (!keys_list.includes(name) && !allowCustom) {
            throw new Error(`[Form] Key ${name} is not a valid key for this form`);
        }

        let newForm = { ...form };
        newForm[name] = value;
        setForm(newForm);
        if(onChange) {
            onChange(newForm);
        }
        handleClearAndErrors(newForm, name);
        
    }, [form, allowCustom, keys, onChange, handleClearAndErrors]);

    const setFormObject = React.useCallback(formObject => {
        const newForm = { ...form, ...formObject };
        setForm(newForm);
        if(onChange) {
            onChange(newForm);
        }
        handleClearAndErrors(newForm, null);
    }, [form, onChange, handleClearAndErrors]);

    return [form, setFormValue, setFormObject, valid, errors, public_validate, setErrors];
}
